import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, InputBase } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { X } from "lucide-react";
import { setLockoutOverride } from "../../api/adminApi";

function pad(n) {
  return String(n).padStart(2, "0");
}

function toDatetimeLocal(unixSec) {
  if (unixSec == null || !Number.isFinite(Number(unixSec))) return "";
  const d = new Date(Number(unixSec) * 1000);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function defaultExpirationLocal() {
  const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  d.setHours(0, 0, 0, 0);
  return toDatetimeLocal(Math.floor(d.getTime() / 1000));
}

const LockoutOverrideModal = ({
  open,
  setOpen,
  orgId,
  initialOverride,
  onSaved,
}) => {
  const [isActive, setIsActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState("");
  const [reason, setReason] = useState("manual exception");
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const active = Boolean(initialOverride?.isActive);
    setIsActive(active || !initialOverride);
    setExpiresAt(
      toDatetimeLocal(initialOverride?.expiration) || defaultExpirationLocal()
    );
    setReason(initialOverride?.reason || "manual exception");
    setError(null);
    setLoader(false);
  }, [open, initialOverride]);

  const submit = async () => {
    setError(null);
    let expiration;
    if (isActive) {
      const ms = new Date(expiresAt).getTime();
      if (!expiresAt || Number.isNaN(ms)) {
        setError("Expiration date and time are required");
        return;
      }
      expiration = Math.floor(ms / 1000);
      if (expiration <= Math.floor(Date.now() / 1000)) {
        setError("Expiration must be in the future");
        return;
      }
      if (!reason.trim()) {
        setError("Reason is required");
        return;
      }
    } else if (expiresAt) {
      const ms = new Date(expiresAt).getTime();
      if (!Number.isNaN(ms)) expiration = Math.floor(ms / 1000);
    }

    setLoader(true);
    try {
      const data = await setLockoutOverride({
        orgId,
        isActive,
        expiration,
        reason: reason.trim() || undefined,
      });
      setOpen(false);
      if (onSaved) onSaved(data.lockoutOverride);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save lockout override"
      );
    } finally {
      setLoader(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      BackdropProps={{ style: { backgroundColor: "rgb(144,150,160,0.8)" } }}
    >
      <div className="px-8 py-6">
        <div className="relative mb-1">
          <div className="lg:text-[20px] text-[18px] font-semibold">
            Lockout override
          </div>
          <div className="absolute top-0 right-0">
            <X
              className="w-[20px] text-gray-400 cursor-pointer"
              onClick={() => setOpen(false)}
            />
          </div>
        </div>
        <p className="text-[13px] text-gray-500 mb-4">
          While this is active, the organization can keep using Twilanswer even
          if the subscription is cancelled or unpaid. The time is in your local
          timezone.
        </p>
        {error && (
          <div className="text-red-500 text-[13px] font-semibold mb-2">
            <ErrorOutline sx={{ fontSize: 16 }} /> {error}
          </div>
        )}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-[14px] text-gray-800">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active
          </label>
          <div>
            <div className="text-[12px] font-semibold text-gray-500 mb-1">
              Expiration
            </div>
            <InputBase
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              sx={{
                bgcolor: "white",
                border: "1px solid #E0E0E0",
                width: "100%",
                px: 2,
                py: 1,
                fontSize: "14px",
                borderRadius: "8px",
              }}
            />
          </div>
          <div>
            <div className="text-[12px] font-semibold text-gray-500 mb-1">
              Reason
            </div>
            <InputBase
              placeholder="Why this override is needed"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{
                bgcolor: "white",
                border: "1px solid #E0E0E0",
                width: "100%",
                px: 2,
                py: 1,
                fontSize: "14px",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button
            onClick={() => setOpen(false)}
            sx={{ textTransform: "none", color: "#6B7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={loader}
            sx={{
              textTransform: "none",
              bgcolor: "#605BFF",
              color: "white",
              borderRadius: "8px",
              px: 2.5,
              "&:hover": { bgcolor: "#4F46E5" },
            }}
          >
            {loader ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default LockoutOverrideModal;
